import type { GetStaticPaths, GetStaticProps } from 'next'
import Link from 'next/link'
import React from 'react'
import {
  Container,
  createPaginationLinkComponent,
  createPrevNextPageLinkComponent,
  PaginationLinks,
  PaginationEllipsis,
  textStyles,
} from '@marketing-sites/ui'
import Head from 'next/head'
import type {
  AllTopicSlugsQuery,
  ArticlesForTopicQuery,
} from '../../../.graphql/generated-types'
import {
  ArticlesForTopicDocument,
  AllTopicSlugsDocument,
} from '../../../.graphql/generated-types'
import {
  extractPageNumberFromParam,
  pageSize,
  siteLocale,
  notEmpty,
} from '@marketing-sites/utils'
import { getGraphQLClient, graphQLClient } from '../../lib/api'
import { fetchGlobalRegionalEditsAsSubstitutions } from '../GlobalRegionalEdits'
import { ArticleListItem } from '../ArticleListItem'
import { ComposeComponentTopicList } from '../ComposeComponentTopicList'
import { TopicPageHeading } from '../TopicPageHeading'
import type { Substitutions } from '../../components/ContentfulRichText'
import { ARTICLES_PAGES_REVALIDATE_SECONDS } from '../articlesRevalidate'
import {
  buildLocalizedVersions,
  buildURLForLocale,
  getXDefaultHref,
} from '../ArticleHead/hreflang'

export interface TopicPageProps {
  data?: ArticlesForTopicQuery | undefined
  substitutions: Substitutions
  pageNumber: number
}

export function TopicPage({ data, substitutions, pageNumber }: TopicPageProps) {
  const topic = data?.composeComponentTopicCollection?.items[0]
  const articleCollection = topic?.linkedFrom?.composePageArticleCollection
  const articleItems = articleCollection?.items.filter(notEmpty)

  const latestArticles =
    articleItems?.map((articleItem) => (
      <li key={articleItem.sys.id}>
        <ArticleListItem data={articleItem} substitutions={substitutions} />
      </li>
    )) ?? []

  const basePath = `/topics/${topic?.slug ?? ''}`

  const TopicPagePaginationLink = createPaginationLinkComponent({
    basePath,
  })

  const TopicPagePaginationPrevLink = createPrevNextPageLinkComponent({
    linkText: '← Prev',
    basePath,
  })
  const TopicPagePaginationNextLink = createPrevNextPageLinkComponent({
    linkText: 'Next →',
    basePath,
  })

  const total = articleCollection?.total ?? 1
  const limit = articleCollection?.limit ?? 1
  const pageCount = Math.ceil(total / limit)
  const topicList = data?.composeComponentTopicListCollection?.items[0]
  if (topic) {
    let title = `Reading Eggs Articles about ${topic.name ?? ''}`
    if (pageNumber > 1) title = `${title}, Page ${pageNumber.toString()}`

    const pathname =
      pageNumber > 1
        ? `/topics/${topic.slug ?? ''}/page-${pageNumber.toString()}/`
        : `/topics/${topic.slug ?? ''}/`
    const localizedVersions = buildLocalizedVersions(pathname)

    return (
      <>
        <Head>
          <title>{title}</title>
          <meta
            name="description"
            content={`Page ${pageNumber.toString()} of Reading Eggs Articles on ${
              topic.name ?? ''
            }`}
            key="description"
          />
          <link
            rel="alternate"
            hrefLang="x-default"
            href={getXDefaultHref(pathname)}
          />
          {localizedVersions.map(({ locale, href }) => (
            <link key={locale} rel="alternate" hrefLang={locale} href={href} />
          ))}
          <link
            rel="canonical"
            href={buildURLForLocale(pathname, siteLocale)}
          />
        </Head>
        <Container>
          <>
            <Link
              href="/articles"
              className={`${textStyles.a_no_underline} mt-6 inline-block`}
            >
              ← Back to articles
            </Link>

            <h2 className={`${textStyles.h2} my-6`}>Reading Eggs Articles</h2>
            <TopicPageHeading data={topic} />

            <ul className="space-y-12">{latestArticles}</ul>
            <div className="my-12 flex justify-center">
              <PaginationLinks
                pageCount={pageCount}
                currentPage={pageNumber}
                PaginationLinkComponent={TopicPagePaginationLink}
                EllipsisComponent={PaginationEllipsis}
                PrevPageLinkComponent={TopicPagePaginationPrevLink}
                NextPageLinkComponent={TopicPagePaginationNextLink}
              />
            </div>
          </>
        </Container>
        <div className="bg-gray">
          {topicList && (
            <div className="bg-slate-50 py-16">
              <Container>
                <>
                  <h2 className={`${textStyles.h2} mb-6 self-start`}>
                    Discover Topics
                  </h2>
                  <ComposeComponentTopicList data={topicList} />
                </>
              </Container>
            </div>
          )}
        </div>
      </>
    )
  }
  return null
}

async function fetchAllTopicSlugs(
  prevResults: AllTopicSlugsQuery[],
): Promise<{ slug: string }[]> {
  const prevResult = prevResults[prevResults.length - 1]
  if (prevResult?.composeComponentTopicCollection) {
    const { total, limit, skip } = prevResult.composeComponentTopicCollection
    const nextSkip = skip + limit
    const canFetchMoreItems = nextSkip < total
    if (canFetchMoreItems) {
      const nextPage = await graphQLClient
        .query(AllTopicSlugsDocument, { skip: nextSkip })
        .toPromise()

      if (nextPage.data) {
        const result = await fetchAllTopicSlugs([...prevResults, nextPage.data])
        return result
      }
    }
  } else {
    const firstPage = await graphQLClient
      .query(AllTopicSlugsDocument, { skip: 0 })
      .toPromise()
    const results = firstPage.data
    if (results) {
      const result = await fetchAllTopicSlugs([results])
      return result
    }
    return []
  }

  return prevResults.flatMap((queryResult) => {
    const topics = queryResult.composeComponentTopicCollection?.items
    if (topics) {
      return topics
        .map((topic) => {
          if (topic?.slug) {
            return { slug: topic.slug }
          }
          return null
        })
        .filter(notEmpty)
    }
    return []
  })
}

export const getStaticPathsForTopicIndexPage: GetStaticPaths = async () => {
  const allTopicSlugs = await fetchAllTopicSlugs([])
  const paths = allTopicSlugs.map((slug) => ({ params: slug }))
  return { paths, fallback: false }
}

export const getStaticPropsForTopicIndexPage: GetStaticProps<
  TopicPageProps
> = async ({ params, preview }) => {
  const slug = params?.['slug']
  const pageNo = params?.['pageNo']
  let pageNumberParam = 1
  if (typeof pageNo === 'string') {
    const pageNumberFromParam = extractPageNumberFromParam(pageNo)
    if (typeof pageNumberFromParam === 'number') {
      pageNumberParam = pageNumberFromParam
    }
  }

  const skip = pageSize * (pageNumberParam - 1)
  const limit = pageSize

  if (typeof slug === 'string') {
    const result = await getGraphQLClient(!!preview)
      .query(ArticlesForTopicDocument, {
        limit,
        skip,
        preview: !!preview,
        locale: siteLocale,
        slug,
      })
      .toPromise()

    const substitutions: Substitutions =
      await fetchGlobalRegionalEditsAsSubstitutions()

    return {
      props: {
        data: result.data,
        substitutions,
        pageNumber: pageNumberParam,
        params,
      },
      revalidate: ARTICLES_PAGES_REVALIDATE_SECONDS,
    }
  }
  return {
    props: { substitutions: {}, pageNumber: 1, params },
    revalidate: ARTICLES_PAGES_REVALIDATE_SECONDS,
  }
}
